<script setup>
import { ref, computed, onMounted } from 'vue'
import { getI18n } from '@/plugins/i18n'

import { usePlanningStore } from '@/stores/planning.store'
import { useWebSiteStore } from '@/stores/web-site.store'
import { useNavigator } from '@/composables/navigator'
import { useChips } from '@/composables/chips'
import { useCurrency } from '@/composables/currency'
import { useArchive } from '@/composables/archive'
import { useDetectDevice } from '@/composables/device'

import ArchiveWarningDialog from '@/components/shared/ArchiveWarningDialog.vue'
import NavigateBack from '@/components/shared/NavigateBack.vue'
import ListItem from '@/components/shared/ListItem.vue'
import ChipTemplate from '@/components/shared/ChipTemplate.vue'
import ListingItemEdit from '@/components/shared/ListingItemEdit.vue'

const props = defineProps({
  modelValue: {
    type: Object,
    required: true,
  },
})

const i18n = getI18n()
const { isMobile } = useDetectDevice()
const planningStore = usePlanningStore()
const webSiteStore = useWebSiteStore()
const { getTagChips } = useChips()
const { getCurrencyString } = useCurrency()
const editItem = ref(null)
const item = ref(props.modelValue || null)

const getCurrencyCode = (currencyRefId) =>
  webSiteStore.currencies.find(c => c.id === currencyRefId)?.code

const unitSymbolById = computed(() =>
  Object.fromEntries(webSiteStore.getLocalizedUnitsOfMeasure().map(u => [u.id, u.symbol])))
const vatLabelById = computed(() =>
  Object.fromEntries(webSiteStore.getLocalizedVatTypes().map(v => [v.id, v.name])))

const serviceName = computed(() => item.value && planningStore.getServiceName(planningStore.getServiceById(item.value.service)))
const unitSymbol = computed(() => unitSymbolById.value[item.value?.unit_of_measure_ref])
const vatLabel = computed(() => vatLabelById.value[item.value?.vat_type_ref])
const priceLabel = computed(() => item.value?.price != null ? getCurrencyString(item.value.price, getCurrencyCode(item.value.currency_ref)) : '—')
const costLabel = computed(() => item.value?.cost != null ? getCurrencyString(item.value.cost, getCurrencyCode(item.value.currency_ref)) : '—')

const updateDataAsync = async() => {
  item.value = (await planningStore.fetchListingItemByIdAsync(item.value.id)).data
}

onMounted(async() => {
  if (!props.modelValue?.id) return
  await updateDataAsync()
})

let changed = false
const onNavigateBack = () => {
  changed ? emit('update') : emit('cancel')
}
const emit = defineEmits(['cancel', 'update'])
useNavigator(onNavigateBack)

const { archiveWarning, archiveAsync } = useArchive({
  onArchive: async(id) => { await planningStore.archiveListingItemAsync(id) },
  onDelete: async(id, config) => { await planningStore.deleteListingItemAsync(id, config) },
})

const onArchive = async(entity) => {
  await archiveAsync(entity.id, entity.archived)
  entity.archived = !entity.archived
}

const onEditItemUpdate = async() => {
  await updateDataAsync()
  editItem.value = null
  changed = true
}
</script>
<template>
  <q-card v-if="item" class="column full-height">
    <q-dialog
      :model-value="editItem !== null"
      :maximized="isMobile"
      persistent
      class="dialog-xs"
    >
      <listing-item-edit
        v-if="editItem !== null"
        :model-value="editItem"
        :listing-id="item.listing"
        @update="onEditItemUpdate"
        @cancel="onEditItemUpdate"
        @delete="editItem = null; emit('update')"
      />
    </q-dialog>
    <archive-warning-dialog
      v-model="archiveWarning"
      @confirm="async() => await archiveAsync(archiveWarning.item.id, false)"
    />
    <navigate-back
      :text="item.name"
      :archived="item.archived"
      @cancel="onNavigateBack"
    >
      <template #right>
        <div style="display: flex; align-items: center;">
          <q-btn
            v-show="!item.archived"
            flat
            round
            icon="mdi-pencil"
            @click="editItem = item"
          />
          <q-btn
            flat
            round
            :icon="item?.archived ? 'mdi-archive-arrow-up' : 'mdi-archive'"
            @click="onArchive(item)"
          />
        </div>
      </template>
    </navigate-back>
    <q-card-section class="scroll col q-pa-none" style="min-height: 0;">
      <template v-if="item.tags?.length">
        <q-card-section class="flex justify-center">
          <chip-template :values="getTagChips(item.tags)" size="md" />
        </q-card-section>
        <q-separator />
      </template>
      <q-card-section>
        <list-item
          v-if="serviceName"
          avatar-icon="mdi-movie-open-outline"
          :label="serviceName"
        />
        <list-item
          v-if="item.listing_name"
          avatar-icon="mdi-book-open-outline"
          :label="item.listing_name"
        />
        <list-item
          v-if="item.description"
          avatar-icon="mdi-text"
          :label="item.description"
        />
      </q-card-section>
      <q-separator />
      <q-card-section>
        <list-item
          avatar-icon="mdi-currency-eur"
          :label="unitSymbol ? `${priceLabel} / ${unitSymbol}` : priceLabel"
        />
        <list-item
          avatar-icon="mdi-cash-minus"
          :label="costLabel"
        />
        <list-item
          v-if="vatLabel"
          avatar-icon="mdi-receipt-text-outline"
          :label="`${i18n.t('common.vat')} ${vatLabel}`"
        />
      </q-card-section>
    </q-card-section>
  </q-card>
</template>
